import { useMutation, useQueryClient } from "@tanstack/react-query";
import { NotificationCenterKeys } from "web_myplann/Api/NotificationCenter/notificationCenterKeys";
import { invalidateNotifications } from "web_myplann/Api/Notifications/notificatioKeys";
import {
	patchNotificationsIdReadV2,
	patchNotificationsReadV2,
} from "web_myplann/Infrastructure/Api/Api";
import { processResponse } from "web_myplann/Infrastructure/Api/ApiUtils";
import {
	markTimelineItemsRead,
	type RecentTimelineData,
} from "web_myplann/NotificationCenter/Utils/markTimelineItemsRead";

type MarkTimelineNotificationsReadVariables = {
	notificationIds: number[];
};

const requestMarkRead = async ({
	notificationIds,
}: MarkTimelineNotificationsReadVariables) => {
	const [single] = notificationIds;
	return notificationIds.length === 1 && single !== undefined
		? await patchNotificationsIdReadV2(single)
		: await patchNotificationsReadV2({ notificationIds });
};

const markNotificationsRead = processResponse(requestMarkRead, {
	skipToastFor: true,
});

export const useMarkTimelineNotificationsReadMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (variables: MarkTimelineNotificationsReadVariables) =>
			markNotificationsRead(variables),
		onMutate: async ({ notificationIds }) => {
			await queryClient.cancelQueries({
				queryKey: NotificationCenterKeys.recentAll,
			});
			const snapshot = queryClient.getQueriesData<RecentTimelineData>({
				queryKey: NotificationCenterKeys.recentAll,
			});
			const ids = new Set(notificationIds);
			queryClient.setQueriesData<RecentTimelineData>(
				{ queryKey: NotificationCenterKeys.recentAll },
				(previous) => markTimelineItemsRead(previous, ids),
			);
			return { snapshot };
		},
		onError: (_error, _variables, context) => {
			for (const [queryKey, previous] of context?.snapshot ?? []) {
				queryClient.setQueryData(queryKey, previous);
			}
		},
		onSettled: () => {
			invalidateNotifications(queryClient);
			void queryClient.invalidateQueries({
				queryKey: NotificationCenterKeys.all,
			});
		},
	});
};
